import { faThumbsUp as farThumbsUp } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import React from "react";
import { connect } from "react-redux";

import { reactionChange } from "@/mk2/containers/EmojiReaction/EmojiReaction.actions";
import {
    AppState,
    getEmojiReactionBreakdown,
    getEmojiReactionTotalCount,
    getEmojiReactionUserReaction,
} from "@/mk2/reducers";
import { HistoryLocationWithState } from "@/mk2/services/browserHistory";
import {
    EMOJI_REACTION_ANGRY,
    EMOJI_REACTION_CARE,
    EMOJI_REACTION_HAHA,
    EMOJI_REACTION_LIKE,
    EMOJI_REACTION_LOVE,
    EMOJI_REACTION_SAD,
    EMOJI_REACTION_WOW,
} from "@/mk/autogenerated/translations/EmojiReaction.955ea1ba57496065a44021cefd3ebcae";

import styles from "./EmojiReaction.mscss";

export const EMOJI_CONFIG: Array<{ type: string; emoji: string; label: string }> = [
    { type: "like", emoji: "👍", label: EMOJI_REACTION_LIKE },
    { type: "love", emoji: "💙", label: EMOJI_REACTION_LOVE },
    { type: "care", emoji: "🤗", label: EMOJI_REACTION_CARE },
    { type: "haha", emoji: "😃", label: EMOJI_REACTION_HAHA },
    { type: "wow", emoji: "😮", label: EMOJI_REACTION_WOW },
    { type: "sad", emoji: "😢", label: EMOJI_REACTION_SAD },
    { type: "angry", emoji: "😠", label: EMOJI_REACTION_ANGRY },
] as const;

const EMOJI_MAP: Record<string, string> = Object.fromEntries(
    EMOJI_CONFIG.map((e) => [e.type, e.emoji]),
);

interface OwnProps {
    /** Custom className for element - e.g. to give it some margin */
    className?: string;

    /** Entity name (e.g. "forum.thread") */
    entity: string;

    /** Entity id */
    id: number;

    /** Disable the button (e.g. own anonymous post) */
    disabled?: boolean;

    /** Remove left padding from the button (applied only when reactionsTotalCount is 0) */
    noLeftPadding?: boolean;

    location: HistoryLocationWithState;
}

interface StateProps {
    userReaction: string | null;
    reactionsBreakdown: Record<string, number>;
    reactionsTotalCount: number;
}

interface DispatchProps {
    onReactionChange(emojiType: string | null, location: HistoryLocationWithState);
}

interface State {
    pickerVisible: boolean;
}

type Props = OwnProps & StateProps & DispatchProps;

function getTopEmojis(
    reactionsBreakdown: Record<string, number>,
    userReaction: string | null,
): string[] {
    const result: string[] = [];

    // User's emoji first
    if (userReaction && EMOJI_MAP[userReaction]) {
        result.push(EMOJI_MAP[userReaction]);
    }

    // Fill remaining slots with top emoji by count (excluding user's)
    const sorted = Object.entries(reactionsBreakdown)
        .filter(([type, count]) => type !== userReaction && count > 0)
        .sort((a, b) => b[1] - a[1]);

    for (const [type] of sorted) {
        if (result.length >= 3) break;
        if (EMOJI_MAP[type]) {
            result.push(EMOJI_MAP[type]);
        }
    }

    return result;
}

export class EmojiReaction extends React.PureComponent<Props, State> {
    public state: State = { pickerVisible: false };

    private suppressUntil = 0;
    private containerRef = React.createRef<HTMLDivElement>();

    public componentDidMount() {
        document.addEventListener("click", this.onDocumentClick, true);
    }

    public componentWillUnmount() {
        document.removeEventListener("click", this.onDocumentClick, true);
    }

    private onDocumentClick = (e: MouseEvent) => {
        if (
            this.state.pickerVisible &&
            this.containerRef.current &&
            !this.containerRef.current.contains(e.target as Node)
        ) {
            this.setState({ pickerVisible: false });
        }
    };

    private onMouseEnter = () => {
        if (!this.props.disabled && Date.now() > this.suppressUntil) {
            this.setState({ pickerVisible: true });
        }
    };

    private onMouseLeave = () => {
        this.setState({ pickerVisible: false });
    };

    private onPickerEmojiClick = (emojiType: string) => {
        const { onReactionChange, location } = this.props;
        this.suppressUntil = Date.now() + 400;
        this.setState({ pickerVisible: false });
        onReactionChange(emojiType, location);
    };

    private onButtonClick = () => {
        const { onReactionChange, location } = this.props;
        if (!this.props.userReaction) {
            onReactionChange("like", location);
            this.setState({ pickerVisible: true });
        } else {
            onReactionChange(null, location);
            this.setState({ pickerVisible: false });
        }
    };

    public render() {
        const { className, userReaction, reactionsTotalCount, disabled, noLeftPadding } =
            this.props;
        const { pickerVisible } = this.state;

        const { reactionsBreakdown } = this.props;
        const isReacted = Boolean(userReaction);
        const topEmojis = getTopEmojis(reactionsBreakdown, userReaction);

        return (
            <div
                ref={this.containerRef}
                className={cx(styles.EmojiReaction, className)}
                onMouseEnter={this.onMouseEnter}
                onMouseLeave={this.onMouseLeave}
            >
                {pickerVisible && (
                    <div className={styles.EmojiReaction__picker}>
                        {EMOJI_CONFIG.map(({ type, emoji, label }) => (
                            <button
                                key={type}
                                type="button"
                                className={cx(
                                    styles.EmojiReaction__pickerBtn,
                                    userReaction === type &&
                                        styles["EmojiReaction__pickerBtn--selected"],
                                )}
                                title={label}
                                aria-label={label}
                                onClick={() => this.onPickerEmojiClick(type)}
                            >
                                {emoji}
                            </button>
                        ))}
                    </div>
                )}
                <button
                    type="button"
                    className={cx(
                        styles.EmojiReaction__btn,
                        isReacted && styles["EmojiReaction__btn--reacted"],
                        noLeftPadding &&
                            reactionsTotalCount === 0 &&
                            styles["EmojiReaction__btn--noLeftPadding"],
                    )}
                    onClick={this.onButtonClick}
                    disabled={disabled}
                    aria-label={reactionsTotalCount === 0 ? EMOJI_REACTION_LIKE : undefined}
                >
                    {reactionsTotalCount > 0 ? (
                        <>
                            <span className={styles.EmojiReaction__stack}>
                                {topEmojis.map((emoji, i) => (
                                    <span
                                        key={i}
                                        className={styles.EmojiReaction__stackEmoji}
                                        style={{ zIndex: topEmojis.length - i }}
                                    >
                                        {emoji}
                                    </span>
                                ))}
                            </span>
                            <span>{reactionsTotalCount}</span>
                        </>
                    ) : (
                        <FontAwesomeIcon
                            icon={farThumbsUp}
                            className={styles.EmojiReaction__icon}
                        />
                    )}
                </button>
            </div>
        );
    }
}

function mapStateToProps(state: AppState, ownProps: OwnProps): StateProps {
    return {
        userReaction: getEmojiReactionUserReaction(state, ownProps.entity, ownProps.id),
        reactionsBreakdown: getEmojiReactionBreakdown(state, ownProps.entity, ownProps.id),
        reactionsTotalCount: getEmojiReactionTotalCount(state, ownProps.entity, ownProps.id),
    };
}

function mapDispatchToProps(dispatch, ownProps: OwnProps): DispatchProps {
    return {
        onReactionChange: (emojiType, location) =>
            dispatch(reactionChange(ownProps.entity, ownProps.id, emojiType, location)),
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(EmojiReaction);
